import {ComponentProps, useCallback, useState} from 'react';
import TablePagination from 'components/Table/TableComponents/TablePagination';

type UsePaginatedDataReturnType<T extends Array<unknown>> = Pick<
    ComponentProps<typeof TablePagination>,
    'pageCurrent' | 'pageStop' | 'onChange' | 'onFirst' | 'onLast'
> & {paginatedData: T};

/**
 *
 * @param data Local data to paginate.
 * @param limit Number of items per page.
 * @param populateEmptyRows If true, will populate the final page with empty rows so that the table size doesn't change.
 */
const usePaginatedData = <T extends Array<unknown>>(
    data: T,
    limit: number = 10,
    populateEmptyRows: boolean = true,
): UsePaginatedDataReturnType<Array<T[0] | null>> => {
    const [page, setPage] = useState(1);

    const onChange = useCallback((pageNumber: number) => {
        setPage(pageNumber);
    }, []);

    const onFirst = useCallback(() => {
        setPage(1);
    }, []);

    const onLast = useCallback(() => {
        setPage(Math.ceil(data.length / limit));
    }, [data.length, limit]);

    const paginatedData =
        // If data length is shorted than limit, return all data
        data.length <= limit
            ? (data as T)
            : (data.slice((page - 1) * limit, page * limit) as T);

    if (populateEmptyRows && page !== 1 && paginatedData.length < limit) {
        paginatedData.push(
            ...Array.from({length: limit - paginatedData.length}).fill(null),
        );
    }

    return {
        onChange,
        onFirst,
        onLast,
        pageCurrent: page,
        pageStop: Math.ceil(data.length / limit),
        paginatedData,
    };
};

export default usePaginatedData;
